import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchAdminBlogPost,
  createBlogPost,
  updateBlogPost,
  publishBlogPost,
  unpublishBlogPost,
  deleteBlogPost,
} from '@/api/blog';
import { fetchPastContests, fetchUpcomingContests, fetchContestMatchupPreview } from '@/api/dfs';
import { BlogPostAdmin, BlogPostCreatePayload, BlogPostUpdatePayload, GameLogLeaderboardEntry, MatchupTopPlayer, PlayerRead } from '@/api/schemas';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';
import Spinner from '@/components/common/Spinner';
import { getAbbreviation } from '@/utils/teams';
import MatchupDefenseTable from '@/components/blog/MatchupDefenseTable';
import MatchupOffenseTable from '@/components/blog/MatchupOffenseTable';
import MatchupPlayersTable from '@/components/blog/MatchupPlayersTable';
import Top10Table from '@/components/blog/Top10Table';
import TopValueTable from '@/components/blog/TopValueTable';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import { userFacingError } from '@/utils/userFacingError';
import { UserActionableError } from '@/utils/appError';

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface BlogEditorFormProps {
  postId: number | null;  // null = new post
  onClose: () => void;
}

const BlogEditorForm: React.FC<BlogEditorFormProps> = ({ postId, onClose }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const showConfirm = useConfirmDialog();

  const isEditing = postId !== null;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [bodyHtml, setBodyHtml] = useState('');
  // `mode` drives which contest dropdown and secondary fields are visible. The inactive mode's
  // state is preserved in memory while editing (so toggling back doesn't nuke work), but only the
  // active mode's fields are included in the saved payload.
  const [mode, setMode] = useState<'preview' | 'recap'>('preview');
  const [recapContestId, setRecapContestId] = useState<string>('');  // '' = none (editorial in recap mode)
  const [previewContestId, setPreviewContestId] = useState<string>(''); // '' = none (editorial in preview mode)
  const [previewMatchBlurbs, setPreviewMatchBlurbs] = useState<Record<string, string>>({});
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [initialPublishedAt, setInitialPublishedAt] = useState<string | null>(null);
  const [savedPostId, setSavedPostId] = useState<number | null>(postId);

  // Player research modal — opens when the admin clicks a player row in one of the
  // inline matchup tables, so they can scout stats while writing a blurb.
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerRead | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Load existing post if editing
  const { data: existingPost, isLoading: isLoadingPost } = useQuery({
    queryKey: ['admin-blog-post', postId],
    queryFn: () => fetchAdminBlogPost(postId!, token),
    enabled: isEditing && !!token,
  });

  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setSlug(existingPost.slug);
      setSlugTouched(true);  // don't auto-overwrite existing slug
      setBodyHtml(existingPost.body_html);
      setRecapContestId(existingPost.recap_contest_id != null ? String(existingPost.recap_contest_id) : '');
      setPreviewContestId(existingPost.preview_contest_id != null ? String(existingPost.preview_contest_id) : '');
      setPreviewMatchBlurbs(existingPost.preview_match_blurbs ?? {});
      // Detect mode from whichever side has data. Preview wins ties; an editorial post
      // (no contest linked) opens in preview mode by default since that's the richer flow.
      if (existingPost.preview_contest_id != null) {
        setMode('preview');
      } else if (existingPost.recap_contest_id != null) {
        setMode('recap');
      } else {
        setMode('preview');
      }
      setCtaLabel(existingPost.next_contest_cta_label || '');
      setCtaUrl(existingPost.next_contest_cta_url || '');
      setInitialPublishedAt(existingPost.published_at ?? null);
    }
  }, [existingPost]);

  // Load past contests for the recap dropdown
  const { data: pastContests = [] } = useQuery({
    queryKey: ['admin-past-contests'],
    queryFn: () => fetchPastContests(token, 90),
    enabled: !!token,
  });

  // Load upcoming contests for the preview dropdown (next 2 weeks).
  const { data: upcomingContests = [] } = useQuery({
    queryKey: ['admin-upcoming-contests'],
    queryFn: () => fetchUpcomingContests(token, 14),
    enabled: !!token,
  });

  // When a preview contest is selected, load its matchup preview so we can render
  // one blurb textarea per match (with a clean header label). Same endpoint the
  // public page uses — nothing admin-specific.
  const previewContestIdNum = previewContestId ? parseInt(previewContestId, 10) : null;
  const recapContestIdNum = recapContestId ? parseInt(recapContestId, 10) : null;
  const { data: previewMatchupData } = useQuery({
    queryKey: ['admin-blog-preview-matchups', previewContestIdNum],
    queryFn: () => fetchContestMatchupPreview(previewContestIdNum!, token),
    enabled: previewContestIdNum != null && !!token,
    refetchOnWindowFocus: false,
  });

  // Recap mode: use the same matchup-preview endpoint as preview mode purely to source
  // the server-resolved competition_id (the rest of the response — defensive ranks + top
  // projected players — is unused here, since recap renders Top10Table off live game logs).
  // Doing this rather than client-side derivation keeps competition resolution identical
  // to preview mode and to the public BlogPostPage's recap flow.
  const { data: recapMatchupData } = useQuery({
    queryKey: ['admin-blog-recap-matchups', recapContestIdNum],
    queryFn: () => fetchContestMatchupPreview(recapContestIdNum!, token),
    enabled: recapContestIdNum != null && !!token,
    refetchOnWindowFocus: false,
  });

  const handleTitleChange = (v: string) => {
    setTitle(v);
    if (!slugTouched) setSlug(slugify(v));
  };

  const handleMatchupPlayerClick = useCallback(
    (entry: MatchupTopPlayer, teamSbId: number, teamName: string) => {
      const player: PlayerRead = {
        id: entry.player_id,
        name: entry.name ?? null,
        nickname: entry.nickname ?? null,
        current_team_id: teamSbId,
        current_team_name: teamName,
        primary_position: entry.position ?? null,
        injury_status: entry.injury_status ?? null,
        injury_details: entry.injury_details ?? null,
      } as PlayerRead;
      setSelectedPlayer(player);
      setIsProfileModalOpen(true);
    },
    []
  );

  const handleTop10PlayerClick = useCallback((entry: GameLogLeaderboardEntry) => {
    const player: PlayerRead = {
      id: entry.player_id,
      name: entry.player_name,
      current_team_name: entry.current_team_name ?? null,
      primary_position: entry.primary_position ?? null,
    } as PlayerRead;
    setSelectedPlayer(player);
    setIsProfileModalOpen(true);
  }, []);

  const handleSlugChange = (v: string) => {
    setSlugTouched(true);
    setSlug(slugify(v));
  };

  const buildPayload = (): BlogPostCreatePayload | BlogPostUpdatePayload => {
    // Strip empty/whitespace-only blurbs so we don't persist dead keys.
    const cleanedBlurbs: Record<string, string> = {};
    for (const [mid, html] of Object.entries(previewMatchBlurbs)) {
      if (typeof html === 'string' && html.trim().length > 0) cleanedBlurbs[mid] = html;
    }
    // Only the active mode's contest link is persisted. The inactive side is always sent as null
    // so switching modes reliably clears previously-saved data.
    const recap_id = mode === 'recap' && recapContestId ? parseInt(recapContestId, 10) : null;
    const preview_id = mode === 'preview' && previewContestId ? parseInt(previewContestId, 10) : null;
    const preview_blurbs =
      mode === 'preview' && Object.keys(cleanedBlurbs).length > 0 ? cleanedBlurbs : null;
    return {
      title: title.trim(),
      slug: slug.trim(),
      body_html: bodyHtml,
      recap_contest_id: recap_id,
      preview_contest_id: preview_id,
      preview_match_blurbs: preview_blurbs,
      next_contest_cta_label: ctaLabel.trim() || null,
      next_contest_cta_url: ctaUrl.trim() || null,
    };
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim() || !slug.trim() || !bodyHtml.trim()) {
        throw new UserActionableError('Title, slug, and body are required.');
      }
      const payload = buildPayload();
      if (savedPostId !== null) {
        return await updateBlogPost(savedPostId, payload, token);
      } else {
        return await createBlogPost(payload as BlogPostCreatePayload, token);
      }
    },
    onSuccess: (saved: BlogPostAdmin) => {
      toast.success(savedPostId ? 'Post updated.' : 'Post created as draft.');
      setSavedPostId(saved.id);
      setInitialPublishedAt(saved.published_at ?? null);
      queryClient.invalidateQueries({ queryKey: ['admin-blog-list'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-post', saved.id] });
    },
    onError: (err: any) => {
      toast.error(userFacingError(err, 'Could not save post.'));
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (savedPostId === null) {
        // Save first if new
        const saved = await createBlogPost(buildPayload() as BlogPostCreatePayload, token);
        setSavedPostId(saved.id);
        return await publishBlogPost(saved.id, token);
      }
      // Save any pending edits first
      await updateBlogPost(savedPostId, buildPayload(), token);
      return await publishBlogPost(savedPostId, token);
    },
    onSuccess: (saved: BlogPostAdmin) => {
      toast.success('Post published.');
      setInitialPublishedAt(saved.published_at ?? null);
      queryClient.invalidateQueries({ queryKey: ['admin-blog-list'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-post', saved.id] });
      queryClient.invalidateQueries({ queryKey: ['blog-list'] });
    },
    onError: (err: any) => {
      toast.error(userFacingError(err, 'Could not publish post.'));
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => {
      if (savedPostId === null) throw new UserActionableError('Save the post before unpublishing it.');
      return unpublishBlogPost(savedPostId, token);
    },
    onSuccess: (saved: BlogPostAdmin) => {
      toast.success('Moved back to draft.');
      setInitialPublishedAt(null);
      queryClient.invalidateQueries({ queryKey: ['admin-blog-list'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-post', saved.id] });
      queryClient.invalidateQueries({ queryKey: ['blog-list'] });
    },
    onError: (err: any) => {
      toast.error(userFacingError(err, 'Could not unpublish post.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (savedPostId === null) throw new UserActionableError('Save the post before deleting it.');
      return deleteBlogPost(savedPostId, token);
    },
    onSuccess: () => {
      toast.success('Post deleted.');
      queryClient.invalidateQueries({ queryKey: ['admin-blog-list'] });
      queryClient.invalidateQueries({ queryKey: ['blog-list'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(userFacingError(err, 'Could not delete post.'));
    },
  });

  const handlePublishClick = useCallback(async () => {
    const ok = await showConfirm({
      title: 'Publish this post?',
      message: 'It will be publicly visible immediately at /blog/' + slug,
      confirmLabel: 'Publish',
    });
    if (ok) publishMutation.mutate();
  }, [showConfirm, publishMutation, slug]);

  const handleDeleteClick = useCallback(async () => {
    const ok = await showConfirm({
      title: 'Delete this post?',
      message: 'This is permanent and cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (ok) deleteMutation.mutate();
  }, [showConfirm, deleteMutation]);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim() || !slug.trim() || !bodyHtml.trim()) {
        throw new UserActionableError('Title, slug, and body are required before previewing.');
      }
      // Save the current form state first so preview reflects latest edits.
      const payload = buildPayload();
      let currentSlug = slug.trim();
      if (savedPostId === null) {
        const created = await createBlogPost(payload as BlogPostCreatePayload, token);
        setSavedPostId(created.id);
        currentSlug = created.slug;
      } else {
        const updated = await updateBlogPost(savedPostId, payload, token);
        currentSlug = updated.slug;
      }
      return currentSlug;
    },
    onSuccess: (currentSlug: string) => {
      window.open(`/blog/${currentSlug}?preview=1`, '_blank', 'noopener,noreferrer');
      queryClient.invalidateQueries({ queryKey: ['admin-blog-list'] });
    },
    onError: (err: any) => {
      toast.error(userFacingError(err, 'Could not open preview.'));
    },
  });

  const isPublished = !!initialPublishedAt;
  const isBusy = saveMutation.isPending || publishMutation.isPending || unpublishMutation.isPending || deleteMutation.isPending || previewMutation.isPending;

  if (isEditing && isLoadingPost) {
    return (
      <div className="flex justify-center py-16"><Spinner size="lg" /></div>
    );
  }

  return (
    <>
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose} className="text-sm text-accent-light-blue hover:underline">
          ← Back to posts
        </button>
        {isPublished && (
          <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-300 text-xs font-semibold">
            PUBLISHED
          </span>
        )}
        {!isPublished && isEditing && (
          <span className="px-2 py-0.5 rounded bg-gray-500/20 text-gray-300 text-xs font-semibold">
            DRAFT
          </span>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 focus:outline-none focus:border-accent-yellow"
            placeholder="Top 10 Performances for Gameweek 33"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Slug <span className="text-gray-500 normal-case">(URL path: /blog/{slug || '…'})</span>
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 font-mono text-sm focus:outline-none focus:border-accent-yellow"
            placeholder="top-10-performances-for-gameweek-33"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Body HTML <span className="text-gray-500 normal-case">(paste raw HTML; Twitter blockquotes supported)</span>
          </label>
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={18}
            className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 font-mono text-xs focus:outline-none focus:border-accent-yellow"
            placeholder="<p>Morgan Gibbs-White was the Player of the Week...</p>"
            required
          />
        </div>

        {/* Mode toggle — Preview vs Recap. Only the active mode's fields render below. */}
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Post type
          </label>
          <div className="inline-flex rounded-md border border-gray-600 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={[
                'px-4 py-2 text-sm font-semibold transition-colors',
                mode === 'preview'
                  ? 'bg-accent-yellow text-primary'
                  : 'bg-primary-dark text-gray-300 hover:bg-primary/60',
              ].join(' ')}
            >
              Preview
            </button>
            <button
              type="button"
              onClick={() => setMode('recap')}
              className={[
                'px-4 py-2 text-sm font-semibold border-l border-gray-600 transition-colors',
                mode === 'recap'
                  ? 'bg-accent-yellow text-primary'
                  : 'bg-primary-dark text-gray-300 hover:bg-primary/60',
              ].join(' ')}
            >
              Recap
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {mode === 'preview'
              ? 'Auto-renders matchup sections (defensive ranks + top 5 players per team) for an upcoming contest.'
              : 'Auto-renders Top 10 leaderboard tables for a past contest.'}
          </p>
        </div>

        {/* Active-mode contest dropdown */}
        {mode === 'preview' ? (
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Preview contest <span className="text-gray-500 normal-case">(upcoming, next 14 days)</span>
            </label>
            <select
              value={previewContestId}
              onChange={(e) => setPreviewContestId(e.target.value)}
              className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-accent-yellow"
            >
              <option value="">— none (editorial post) —</option>
              {upcomingContests.map(c => (
                <option key={c.id} value={c.id}>
                  #{c.id} · {c.name} ({c.contest_date})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Recap contest <span className="text-gray-500 normal-case">(past 90 days)</span>
            </label>
            <select
              value={recapContestId}
              onChange={(e) => setRecapContestId(e.target.value)}
              className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-accent-yellow"
            >
              <option value="">— none (editorial post) —</option>
              {pastContests.map(c => (
                <option key={c.id} value={c.id}>
                  #{c.id} · {c.name} ({c.contest_date})
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Next contest CTA label
          </label>
          <input
            type="text"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-accent-yellow"
            placeholder="Create your lineup for Gameweek 34 here"
          />
        </div>

        {/* Per-match blurbs — only in preview mode when a contest is selected. One textarea per
            match in the contest, auto-ordered by kickoff. Empty blurbs aren't persisted. */}
        {mode === 'preview' && previewContestIdNum != null && previewMatchupData && previewMatchupData.matches.length > 0 && (
          <div className="pt-2">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Matchup blurbs <span className="text-gray-500 normal-case">(optional HTML; renders under each matchup's tables)</span>
            </label>
            <div className="space-y-3">
              {previewMatchupData.matches.map((m) => {
                const homeAbbr = getAbbreviation(m.home_team.team_name) || m.home_team.team_name;
                const awayAbbr = getAbbreviation(m.away_team.team_name) || m.away_team.team_name;
                const kickoff = m.kickoff_time_utc
                  ? new Date(m.kickoff_time_utc).toLocaleString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    })
                  : 'TBD';
                const key = String(m.match_id);
                return (
                  <div key={m.match_id} className="bg-primary-dark/60 border border-gray-700/60 rounded p-3">
                    <div className="text-xs font-heading font-bold text-accent-yellow uppercase tracking-[1.5px] mb-3">
                      {homeAbbr} vs {awayAbbr}
                      <span className="text-gray-400 font-normal normal-case tracking-normal ml-2">
                        · {kickoff}
                      </span>
                    </div>
                    {/* Live tables — identical to what renders on the public page, so the admin
                        has full context while writing the blurb. */}
                    <MatchupDefenseTable home={m.home_team} away={m.away_team} />
                    <MatchupOffenseTable home={m.home_team} away={m.away_team} />
                    <MatchupPlayersTable
                      heading={`TOP 5 ${m.home_team.team_name.toUpperCase()}`}
                      players={m.home_team.top_players}
                      onPlayerClick={(p) => handleMatchupPlayerClick(p, m.home_team.team_sb_id, m.home_team.team_name)}
                    />
                    <MatchupPlayersTable
                      heading={`TOP 5 ${m.away_team.team_name.toUpperCase()}`}
                      players={m.away_team.top_players}
                      onPlayerClick={(p) => handleMatchupPlayerClick(p, m.away_team.team_sb_id, m.away_team.team_name)}
                    />
                    <label className="block text-caption font-semibold text-gray-400 uppercase tracking-wider mt-2 mb-1">
                      Blurb
                    </label>
                    <textarea
                      value={previewMatchBlurbs[key] ?? ''}
                      onChange={(e) =>
                        setPreviewMatchBlurbs((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      rows={4}
                      className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 font-mono text-xs focus:outline-none focus:border-accent-yellow"
                      placeholder={`<p>Blurb for ${homeAbbr} vs ${awayAbbr}...</p>`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recap mode: render the same Top 10 tables that will appear on the public post,
            so the admin can see who they're writing about. Visual parity with the preview block above. */}
        {mode === 'recap' && recapContestIdNum != null && (
          <div className="pt-2">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Top 10 tables <span className="text-gray-500 normal-case">(live preview — identical to the published post)</span>
            </label>
            <div className="bg-primary-dark/60 border border-gray-700/60 rounded p-3 space-y-2">
              <Top10Table
                heading="TOP 10 OVERALL"
                contestId={recapContestIdNum}
                position={null}
                token={token}
                onPlayerClick={handleTop10PlayerClick}
              />
              <Top10Table
                heading="TOP 10 FORWARDS"
                contestId={recapContestIdNum}
                position="FW"
                token={token}
                onPlayerClick={handleTop10PlayerClick}
              />
              <Top10Table
                heading="TOP 10 MIDFIELDERS"
                contestId={recapContestIdNum}
                position="MF"
                token={token}
                onPlayerClick={handleTop10PlayerClick}
              />
              <Top10Table
                heading="TOP 10 DEFENDERS"
                contestId={recapContestIdNum}
                position="DF"
                token={token}
                onPlayerClick={handleTop10PlayerClick}
              />
              <TopValueTable
                heading="TOP 10 VALUE"
                contestId={recapContestIdNum}
                token={token}
                onPlayerClick={handleTop10PlayerClick}
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Next contest CTA URL
          </label>
          <input
            type="text"
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
            className="w-full px-3 py-2 bg-primary-dark border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-accent-yellow"
            placeholder="/dfs/build/42"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700/60">
          <button
            type="submit"
            disabled={isBusy}
            className="btn-secondary disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Draft'}
          </button>

          <button
            type="button"
            onClick={() => previewMutation.mutate()}
            disabled={isBusy}
            className="btn-primary disabled:opacity-50"
            title="Save current edits and open a preview in a new tab"
          >
            {previewMutation.isPending ? 'Opening…' : 'Preview Draft'}
          </button>

          {!isPublished && (
            <button
              type="button"
              onClick={handlePublishClick}
              disabled={isBusy}
              className="bg-accent-yellow hover:bg-yellow-500 text-primary font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50"
            >
              {publishMutation.isPending ? 'Publishing…' : 'Publish'}
            </button>
          )}

          {isPublished && savedPostId !== null && (
            <>
              <button
                type="button"
                onClick={() => unpublishMutation.mutate()}
                disabled={isBusy}
                className="btn-primary disabled:opacity-50"
              >
                {unpublishMutation.isPending ? 'Moving…' : 'Move to Draft'}
              </button>
              <a
                href={`/blog/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center"
              >
                View Live →
              </a>
            </>
          )}

          {savedPostId !== null && (
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isBusy}
              className="btn-danger ml-auto disabled:opacity-50"
            >
              Delete
            </button>
          )}
        </div>
      </form>
    </div>

    <PlayerProfileModal
      isOpen={isProfileModalOpen}
      onClose={() => setIsProfileModalOpen(false)}
      player={selectedPlayer}
      competitionId={mode === 'recap' ? (recapMatchupData?.competition_id ?? null) : (previewMatchupData?.competition_id ?? null)}
      competitionName={null}
      isMyPlayer={false}
      isActionLoading={false}
      dfsContestId={mode === 'recap' ? recapContestIdNum : previewContestIdNum}
    />
    </>
  );
};

export default BlogEditorForm;
